import { formattedUTCDate, type ColumnOrColumnGroup } from '@talos/kyoko';
import type { ValueFormatterParams, ValueGetterParams } from 'ag-grid-community';
import type { PriceGridLadderColumnConfig } from '../../columns/priceGridMarketDataQtyColumns';
import { getMultilegSyntheticProductTypeLabel } from '../../filters';
import {
  getPriceGridLegMarketDataColumn,
  getPriceGridLegMarketDataColumnDynamics,
  getPriceGridMarketDataColumn,
  getPriceGridMarketDataColumnDynamics,
  type PriceGridMarketDataColumnHandlers,
} from '../../hooks/dynamics';
import type { PriceGridEntity } from '../../PriceGridEntity';

const SECURITY_MARKET_DATA_SYMBOL_COL_ID = 'SecurityMarketData-Symbol';
const SECURITY_MARKET_DATA_LEG_1_COL_ID = 'SecurityMarketData-Leg1';
const SECURITY_MARKET_DATA_LEG_2_COL_ID = 'SecurityMarketData-Leg2';

export function getMultilegPriceGridColumns({
  handlers,
  isBBOEnabled,
  ladderConfig,
  showLegMarketSelector,
}: {
  handlers?: PriceGridMarketDataColumnHandlers;
  isBBOEnabled: boolean;
  ladderConfig: PriceGridLadderColumnConfig;
  showLegMarketSelector: boolean;
}): ColumnOrColumnGroup[] {
  return [
    {
      type: 'custom',
      id: 'ProductType',
      field: 'security.MultilegDetails.SyntheticProductType',
      title: 'Product',
      initialWidth: 120,
      sortable: true,
      params: {
        valueGetter: ({ data }: ValueGetterParams<PriceGridEntity>) =>
          getMultilegSyntheticProductTypeLabel(data?.security.MultilegDetails?.SyntheticProductType),
      },
    },
    {
      type: 'text',
      id: 'Quantity',
      field: 'bucket.size',
      title: 'Qty',
      initialWidth: 100,
      sortable: false,
      suppressColumnsToolPanel: true,
    },
    {
      type: 'custom',
      id: 'Expiration',
      field: 'security.Expiration',
      title: 'Expiry',
      initialWidth: 120,
      initialHide: true,
      sortable: true,
      params: {
        valueFormatter: ({ value }: ValueFormatterParams) =>
          typeof value === 'string' ? formattedUTCDate(value, '{dd}-{Mon}-{yy}').toUpperCase() : '',
      },
    },
    getPriceGridMarketDataColumn(
      getPriceGridMarketDataColumnDynamics({
        colID: SECURITY_MARKET_DATA_SYMBOL_COL_ID,
        title: 'Price',
      }),
      handlers,
      { includeActionColumns: true, includeRFQColumn: false, isBBOEnabled, ladderConfig }
    ),
    getPriceGridLegMarketDataColumn(
      getPriceGridLegMarketDataColumnDynamics({
        colID: SECURITY_MARKET_DATA_LEG_1_COL_ID,
        title: 'Leg 1',
        legIndex: 0,
      }),
      handlers,
      {
        includeActionColumns: true,
        includeLegMarketSelectorColumn: showLegMarketSelector,
        includeRFQColumn: false,
        isBBOEnabled,
        ladderConfig,
      }
    ),
    getPriceGridLegMarketDataColumn(
      getPriceGridLegMarketDataColumnDynamics({
        colID: SECURITY_MARKET_DATA_LEG_2_COL_ID,
        title: 'Leg 2',
        legIndex: 1,
      }),
      handlers,
      {
        includeActionColumns: true,
        includeLegMarketSelectorColumn: showLegMarketSelector,
        includeRFQColumn: false,
        isBBOEnabled,
        ladderConfig,
      }
    ),
  ];
}
